import {Toggle, Checkbox} from '~/src/design-system/BaseComponents'
import {useFormikContext} from 'formik'
import React from 'react'
import {useLocation} from 'react-router-dom'
import styled from 'styled-components'
import {Button, ButtonContainer} from '~/src/design-system'
import {FormLabel} from '../../ui/form/FormLabel'
import {RequiredAsterisk} from '../../ui/form/RequiredAsterisk'
import {PhoneInput} from '../../ui/inputs/PhoneInput'
import {FormikGooglePlacesCombobox, FormikInput} from '~/src/design-system-shims'

const FormContainer = styled.div`
  background: white;
  border-radius: var(--radius-sm);
  box-shadow: 0 12px 24px 0 rgba(16, 30, 115, 0.06);
`

const ToggleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: var(--space-4);
  margin-top: var(--space-2);
`

const ToggleLabel = styled.span`
  color: var(--neutral-800);
  font-family: 'Open Sans';
  font-size: 14px;
  font-weight: 500;
`

const Divider = styled.div`
  height: 1px;
  background-color: var(--neutral-300);
  margin: var(--space-8) 0;
`

// Grid replacement components
const GridContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--space-6);
  padding: var(--space-8);
  @media (max-width: 960px) {
    grid-template-columns: 1fr;
  }
`

const LeftColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-6);
`

const RightColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-6);
`

const TwoColumnGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--space-4);

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`

const PhoneExtensionGrid = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: var(--space-4);
`

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
`

const CheckboxContainer = styled.div`
  margin-top: var(--space-4);
`

const AddressContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--space-6);
  padding: 0px var(--space-8);

  @media (max-width: 960px) {
    grid-template-columns: 1fr;
  }
`

const MarkupContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: var(--space-6);
  /* STFFLG-3700: match the horizontal inset of the other field sections
     (GridContainer/AddressContainer use var(--space-8)) so the Mark Up field
     lines up with them instead of hugging the card's left edge. */
  padding: 0px var(--space-8);
  max-width: 360px;
`

// STFFLG-3700: the `%` is the DS Input's built-in iconRight slot (a flex sibling of
// the input), not an absolute overlay — so the value can never run under it.
const PercentAdornment = styled.span`
  color: var(--text-secondary);
  font-size: var(--fs-body);
`

const ColoredTextPleaseEnter = styled.p`
  color: var(--brand-cta);
  font-size: 14px;
  font-weight: 400;
  margin-top: var(--space-4);
  padding: 0px var(--space-8);
`

interface IFormValues {
  name: string
  phoneNumber: string
  extension?: string
  website?: string
  adminFirstName: string
  adminLastName: string
  adminEmail: string
  isMsp: boolean
  isEor: boolean
  parentAgencyId?: string // New field for hierarchy
  markupPercentage?: number // New field for branch agencies
  address: {
    line1: string
    city: string
    state: string
    zipCode: string
    display: string
  }
  billingAddress: {
    line1: string
    city: string
    state: string
    zipCode: string
    display: string
  }
  sameAsAgencyAddress: boolean
}

interface AgencyFormProps {
  isEditMode?: boolean
  isCreateMode?: boolean // New: Explicitly indicate create mode
  initialValues?: Partial<IFormValues>
  onCancel?: () => void
  isSubmitting?: boolean
  isValid?: boolean
  isDirty?: boolean
  showParentAgencyField?: boolean // Control visibility of parent agency field
  parentAgencyOptions?: Array<{id: string; name: string}> // Options for parent agency
  setIsEditing?: (isEditing: boolean) => void // Optional: not needed in create mode
  isBranch?: boolean // Show markup field and adjust labels for branch creation
}

export const AgencyForm: React.FC<AgencyFormProps> = ({
  isEditMode = false,
  isCreateMode = false,
  onCancel,
  isSubmitting = false,
  isValid = true,
  isDirty = false,

  showParentAgencyField = false,
  parentAgencyOptions = [],
  setIsEditing,
  isBranch = false,
}) => {
  const {values, setFieldValue} = useFormikContext<IFormValues>()
  const location = useLocation()
  const isNewBranch = location.pathname.includes('/branches/new')

  // In create mode, fields should always be enabled
  // In edit mode, fields are enabled based on isEditMode state
  const fieldsEnabled = isCreateMode || isEditMode

  const handleSameAddressChange = React.useCallback(
    (checked: boolean) => {
      void setFieldValue('sameAsAgencyAddress', checked)
      if (checked) {
        // Copy the exact agency address object to billing address (unconditional, matching
        // CompanyForm — the old `address.line1` guard skipped the copy for line1-less addresses).
        void setFieldValue('billingAddress', values.address)
      } else if (!checked) {
        // Clear billing address when unchecked
        void setFieldValue('billingAddress', {
          line1: '',
          city: '',
          state: '',
          zipCode: '',
          display: '',
        })
      }
    },
    [setFieldValue, values.address],
  )

  // Watch for changes to agency address to keep billing address in sync when checkbox is checked
  React.useEffect(() => {
    if (values.sameAsAgencyAddress) {
      // Checkbox is checked — keep billing address in sync with the agency address
      void setFieldValue('billingAddress', values.address)
    }
  }, [values.address?.line1, values.address?.display, values.sameAsAgencyAddress, setFieldValue])

  // Watch for manual changes to billing address to uncheck the checkbox
  React.useEffect(() => {
    if (values.sameAsAgencyAddress && values.billingAddress.line1 && values.address.line1) {
      // Check if billing address has been manually changed
      const addressesAreSame =
        values.billingAddress.line1 === values.address.line1 &&
        values.billingAddress.city === values.address.city &&
        values.billingAddress.state === values.address.state &&
        values.billingAddress.zipCode === values.address.zipCode

      if (!addressesAreSame) {
        // Billing address was manually changed, uncheck the checkbox
        void setFieldValue('sameAsAgencyAddress', false)
      }
    }
  }, [
    values.billingAddress?.line1,
    values.billingAddress?.display,
    values.address?.line1,
    values.sameAsAgencyAddress,
    setFieldValue,
  ])

  return (
    <FormContainer>
      {isCreateMode && (
        <ColoredTextPleaseEnter>
          Please enter all required agency information
        </ColoredTextPleaseEnter>
      )}

      <GridContainer>
        {/* Left Column */}
        <LeftColumn>
          <FieldGroup>
            <FormLabel>
              Agency Name <RequiredAsterisk />
            </FormLabel>
            <FormikInput
              data-testid="agencyForm-name-input"
              aria-label="agencyForm-name-input"
              name="name"
              placeholder="JS North America"
              disabled={!fieldsEnabled}
            />
          </FieldGroup>

          <PhoneExtensionGrid>
            <FieldGroup>
              <FormLabel>
                Agency Phone <RequiredAsterisk />
              </FormLabel>
              <PhoneInput
                data-testid="agencyForm-phoneNumber-input"
                aria-label="agencyForm-phoneNumber-input"
                name="phoneNumber"
                placeholder="(555) 123-4567"
                fullWidth
                disabled={!fieldsEnabled}
              />
            </FieldGroup>
            <FieldGroup>
              <FormLabel>Extension</FormLabel>
              <FormikInput
                data-testid="agencyForm-phoneExtension-input"
                aria-label="agencyForm-phoneExtension-input"
                name="phoneExtension"
                placeholder=""
                disabled={!fieldsEnabled}
                inputProps={{
                  inputMode: 'numeric',
                  pattern: '[0-9]*',
                }}
                onInput={(e: React.FormEvent<HTMLInputElement>) => {
                  const target = e.target as HTMLInputElement
                  target.value = target.value.replace(/[^0-9]/g, '')
                }}
              />
            </FieldGroup>
          </PhoneExtensionGrid>

          <FieldGroup>
            <FormLabel>Agency Website</FormLabel>
            <FormikInput
              data-testid="agencyForm-website-input"
              aria-label="agencyForm-website-input"
              name="website"
              placeholder="www.forever21.com"
              disabled={!fieldsEnabled}
            />
          </FieldGroup>

          {/* {showParentAgencyField && (
            <FieldGroup>
              <FormLabel>Parent Agency</FormLabel>
              <FormikSelect
                name="parentAgencyId"
                fullWidth
                displayEmpty
                disabled={!fieldsEnabled}
                inputProps={{
                  MenuProps: {
                    style: {maxHeight: 300},
                    id: 'id-menu',
                    anchorOrigin: {
                      vertical: 'bottom',
                      horizontal: 'center',
                    },
                  },
                }}
                SelectProps={{
                  displayEmpty: true,
                }}>
                <option value="">
                  <em>None (Top-level agency)</em>
                </option>
                {parentAgencyOptions.map((agency) => (
                  <option key={agency.id} value={agency.id}>
                    <Tooltip title={agency.name}>
                      <EllipsisText>{agency.name}</EllipsisText>
                    </Tooltip>
                  </option>
                ))}
              </FormikSelect>
            </FieldGroup>
          )} */}
        </LeftColumn>

        {/* Right Column */}
        <RightColumn>
          <TwoColumnGrid>
            <FieldGroup>
              <FormLabel>
                Admin First Name <RequiredAsterisk />
              </FormLabel>
              <FormikInput
                data-testid="agencyForm-adminFirstName-input"
                aria-label="agencyForm-adminFirstName-input"
                name="adminFirstName"
                placeholder="John"
                disabled={!fieldsEnabled}
              />
            </FieldGroup>
            <FieldGroup>
              <FormLabel>
                Admin Last Name <RequiredAsterisk />
              </FormLabel>
              <FormikInput
                data-testid="agencyForm-adminLastName-input"
                aria-label="agencyForm-adminLastName-input"
                name="adminLastName"
                placeholder="Smith"
                disabled={!fieldsEnabled}
              />
            </FieldGroup>
          </TwoColumnGrid>

          <FieldGroup>
            <FormLabel>
              Admin Email Address <RequiredAsterisk />
            </FormLabel>
            <FormikInput
              data-testid="agencyForm-adminEmail-input"
              aria-label="agencyForm-adminEmail-input"
              name="adminEmail"
              placeholder="john_smith@email.com"
              disabled={!isCreateMode && !isNewBranch}
            />
          </FieldGroup>

          <TwoColumnGrid>
            <FieldGroup>
              <FormLabel>
                MSP <RequiredAsterisk />:
              </FormLabel>
              <ToggleContainer>
                <ToggleLabel>Off</ToggleLabel>
                <Toggle
                  data-testid="agencyForm-isMsp-switch"
                  aria-label="agencyForm-isMsp-switch"
                  checked={values.isMsp}
                  onChange={(e) => setFieldValue('isMsp', e.target.checked)}
                  // Parent agencies: MSP always ON and not clickable
                  // Branch agencies: MSP always OFF and not clickable
                  disabled={true}
                />
                <ToggleLabel>On</ToggleLabel>
              </ToggleContainer>
            </FieldGroup>

            <FieldGroup>
              <FormLabel>
                EOR <RequiredAsterisk />:
              </FormLabel>
              <ToggleContainer>
                <ToggleLabel>Off</ToggleLabel>
                <Toggle
                  data-testid="agencyForm-isEor-switch"
                  aria-label="agencyForm-isEor-switch"
                  checked={values.isEor}
                  onChange={(e) => setFieldValue('isEor', e.target.checked)}
                  // Parent agencies: EOR always ON and not clickable
                  // Branch agencies: EOR is clickable (can toggle ON/OFF)
                  disabled={!isBranch ? true : !fieldsEnabled}
                />
                <ToggleLabel>On</ToggleLabel>
              </ToggleContainer>
            </FieldGroup>
          </TwoColumnGrid>
        </RightColumn>
      </GridContainer>

      <Divider />

      {/* Address Section */}
      <AddressContainer>
        <FieldGroup>
          <FormLabel>
            Location Address <RequiredAsterisk />
          </FormLabel>
          <FormikGooglePlacesCombobox
            data-testid="agencyForm-address-input"
            aria-label="agencyForm-address-input"
            name="address"
            address={values.address}
            setFieldValue={setFieldValue}
            isCityStateBoolean={false}
            initialEditValues={undefined}
            disabled={!fieldsEnabled}
          />
        </FieldGroup>

        <FieldGroup>
          <FormLabel>
            Billing Address <RequiredAsterisk />
          </FormLabel>
          {values.sameAsAgencyAddress ? (
            // When "same as agency address" is checked, show a plain disabled input with the
            // copied value (falling back to the agency address) — the disabled Google Places
            // combobox doesn't surface the value, which is why the address wasn't showing.
            <FormikInput
              data-testid="agencyForm-billingAddress-same-input"
              aria-label="agencyForm-billingAddress-same-input"
              name="billingAddress.display"
              value={values.billingAddress?.display || values.address?.display || ''}
              disabled
            />
          ) : (
            <FormikGooglePlacesCombobox
              key="billing-different"
              data-testid="agencyForm-billingAddress-input"
              aria-label="agencyForm-billingAddress-input"
              name="billingAddress"
              address={values.billingAddress}
              setFieldValue={setFieldValue}
              isCityStateBoolean={false}
              initialEditValues={undefined}
              disabled={!fieldsEnabled}
            />
          )}
          <CheckboxContainer>
            <Checkbox
              data-testid="agencyForm-sameAsAgencyAddress-checkbox"
              aria-label="agencyForm-sameAsAgencyAddress-checkbox"
              checked={values.sameAsAgencyAddress}
              onChange={(e) => handleSameAddressChange(e.target.checked)}
              disabled={!fieldsEnabled}
              label="Same as Agency Address"
            />
          </CheckboxContainer>
        </FieldGroup>
      </AddressContainer>

      {/* Markup field - only show for branches */}
      {isNewBranch && (
        <MarkupContainer>
          <FormLabel>
            Mark Up <RequiredAsterisk />
          </FormLabel>
          <FormikInput
            data-testid="agencyForm-markupPercentage-input"
            aria-label="agencyForm-markupPercentage-input"
            name="markupPercentage"
            type="number"
            inputProps={{min: 0, max: 100}}
            placeholder="0"
            disabled={!isNewBranch}
            iconRight={<PercentAdornment>%</PercentAdornment>}
          />
        </MarkupContainer>
      )}

      {/* Agency form buttons - show in both create and edit modes */}
      {fieldsEnabled && (
        <ButtonContainer
          style={{
            paddingBottom: 'var(--space-6)',
            paddingRight: 'var(--space-6)',
            justifyContent: 'center',
          }}>
          <Button
            variant="secondary"
            data-testid="agencyForm-cancel-button"
            aria-label="agencyForm-cancel-button"
            type="button"
            onClick={onCancel}>
            Cancel
          </Button>
          <Button
            variant="primary"
            data-testid="agencyForm-save-button"
            aria-label="agencyForm-save-button"
            type="submit"
            disabled={isSubmitting || !isValid || !isDirty}>
            {isCreateMode ? 'Create Agency' : 'Save Changes'}
          </Button>
        </ButtonContainer>
      )}
    </FormContainer>
  )
}

export type {IFormValues as AgencyFormValues}
